import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    FaCalendarAlt, FaArrowLeft, FaUser, FaTag,
    FaFacebookF, FaTwitter, FaWhatsapp, FaLinkedinIn,
    FaTelegramPlane, FaLink, FaCheck, FaShareAlt,
    FaSpinner, FaExclamationTriangle
} from 'react-icons/fa';
import { artikelPublicService } from '../services/artikelPublicService';

const BeritaDetail = () => {
    const { id, slug } = useParams();
    const navigate = useNavigate();

    const [berita, setBerita] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    // ============ FETCH DETAIL DARI API ============
    useEffect(() => {
        const loadDetail = async () => {
            setLoading(true);
            setError('');

            try {
                let result;

                // Prioritas: fetch berdasarkan slug (lebih SEO friendly)
                if (slug) {
                    result = await artikelPublicService.getArtikelBySlug(slug);
                } else if (id) {
                    // Fallback: fetch berdasarkan ID
                    // Sesuaikan method ini dengan service Anda
                    // Misal: artikelPublicService.getArtikelById(id)
                    result = await artikelPublicService.getArtikelById(id);
                }

                if (result && result.success) {
                    setBerita(result.data.data || result.data);
                } else {
                    setError(result?.message || 'Berita tidak ditemukan');
                }
            } catch (err) {
                console.error('Error fetching berita detail:', err);
                setError('Gagal memuat berita. Silakan coba lagi.');
            } finally {
                setLoading(false);
            }
        };

        if (id || slug) {
            loadDetail();
        }
    }, [id, slug]);

    // ============ FUNGSI SHARE ============
    const getBeritaUrl = () => window.location.href;

    const handleShare = (platform) => {
        const url = encodeURIComponent(getBeritaUrl());
        const title = encodeURIComponent(berita?.judul || berita?.title || 'Berita BPKAD Kota Magelang');

        const shareUrls = {
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
            twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
            whatsapp: `https://wa.me/?text=${title}%20${url}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
            telegram: `https://t.me/share/url?url=${url}&text=${title}`,
        };

        if (platform === 'copy') {
            navigator.clipboard.writeText(getBeritaUrl()).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }).catch((err) => {
                console.error('Gagal menyalin link:', err);
            });
            return;
        }

        window.open(shareUrls[platform], '_blank', 'noopener,noreferrer,width=600,height=500');
    };

    // ============ HELPER ============
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return path;
    };

    // ============ LOADING STATE ============
    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <FaSpinner className="animate-spin text-4xl text-[#0D9488] mx-auto mb-4" />
                    <p className="text-gray-600">Memuat berita...</p>
                </div>
            </div>
        );
    }

    // ============ ERROR / NOT FOUND STATE ============
    if (error || !berita) {
        return (
            <div className="container mx-auto px-4 py-16 text-center bg-gray-50 min-h-screen">
                <FaExclamationTriangle className="text-5xl text-yellow-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-700 mb-2">
                    {error || 'Berita tidak ditemukan'}
                </h2>
                <p className="text-gray-500 mb-6">
                    ID: {id} | Slug: {slug}
                </p>
                <Link
                    to="/informasi/artikel"
                    className="inline-block bg-[#0D9488] text-white px-6 py-3 rounded-lg hover:bg-[#0F766E] transition-colors"
                >
                    Kembali ke daftar berita
                </Link>
            </div>
        );
    }

    const judul = berita.judul || berita.title;
    const konten = berita.konten || berita.content;
    const ringkasan = berita.ringkasan || berita.summary;
    const gambar = berita.gambar || berita.image || berita.thumbnail;
    const kategori = berita.kategori || berita.category;
    const penulis = berita.penulis || berita.author;
    const tanggal = berita.published_at || berita.date || berita.created_at;
    const views = berita.views || 0;

    return (
        <div className="bg-gray-50 min-h-screen py-12">
            <div className="container mx-auto px-4 max-w-7xl">
                {/* Tombol Kembali */}
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-[#0D9488] hover:text-[#0F766E] mb-6 transition-colors font-medium"
                >
                    <FaArrowLeft /> Kembali
                </button>

                <article className="bg-white rounded-xl shadow-lg overflow-hidden">
                    {/* Featured Image */}
                    {gambar && (
                        <img
                            src={getImageUrl(gambar)}
                            alt={judul}
                            className="w-full h-80 object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    )}

                    <div className="p-6 md:p-8">
                        {/* Meta Info */}
                        <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                                <FaCalendarAlt className="text-[#0D9488]" />
                                {formatDate(tanggal)}
                            </span>
                            {kategori && (
                                <span className="flex items-center gap-1">
                                    <FaTag className="text-[#0D9488]" />
                                    <span className="bg-[#0D9488] text-white px-3 py-1 rounded-full text-xs capitalize">
                                        {kategori}
                                    </span>
                                </span>
                            )}
                            {penulis && (
                                <span className="flex items-center gap-1">
                                    <FaUser className="text-[#0D9488]" />
                                    {penulis}
                                </span>
                            )}
                        </div>

                        {/* Judul */}
                        <h1 className="text-3xl md:text-4xl font-bold text-[#1E3A8A] mb-6">
                            {judul}
                        </h1>

                        {/* ============ SHARE TO SOCIAL MEDIA (ATAS) ============ */}
                        <div className="flex flex-wrap items-center gap-3 py-4 mb-6 border-y border-gray-200">
                            <span className="flex items-center text-gray-600 font-semibold text-sm">
                                <FaShareAlt className="mr-2 text-[#0D9488]" /> Bagikan:
                            </span>

                            <button
                                onClick={() => handleShare('facebook')}
                                aria-label="Bagikan ke Facebook"
                                title="Bagikan ke Facebook"
                                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                            >
                                <FaFacebookF />
                            </button>

                            <button
                                onClick={() => handleShare('twitter')}
                                aria-label="Bagikan ke Twitter"
                                title="Bagikan ke Twitter"
                                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1DA1F2] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                            >
                                <FaTwitter />
                            </button>

                            <button
                                onClick={() => handleShare('whatsapp')}
                                aria-label="Bagikan ke WhatsApp"
                                title="Bagikan ke WhatsApp"
                                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                            >
                                <FaWhatsapp />
                            </button>

                            <button
                                onClick={() => handleShare('linkedin')}
                                aria-label="Bagikan ke LinkedIn"
                                title="Bagikan ke LinkedIn"
                                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0A66C2] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                            >
                                <FaLinkedinIn />
                            </button>

                            <button
                                onClick={() => handleShare('telegram')}
                                aria-label="Bagikan ke Telegram"
                                title="Bagikan ke Telegram"
                                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0088CC] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                            >
                                <FaTelegramPlane />
                            </button>

                            <button
                                onClick={() => handleShare('copy')}
                                aria-label="Salin link berita"
                                title="Salin link"
                                className={`w-10 h-10 flex items-center justify-center rounded-full text-white hover:scale-110 hover:shadow-lg transition-all duration-200 ${copied ? 'bg-green-600' : 'bg-gray-600'
                                    }`}
                            >
                                {copied ? <FaCheck /> : <FaLink />}
                            </button>

                            {copied && (
                                <span className="text-sm text-green-600 font-medium animate-pulse">
                                    Link berhasil disalin!
                                </span>
                            )}
                        </div>

                        {/* Content */}
                        <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed break-words overflow-hidden
                                        [&>p]:mb-4 [&>p]:leading-7
                                        [&>h2]:mt-8 [&>h2]:mb-4 [&>h2]:text-2xl [&>h2]:font-bold
                                        [&>h3]:mt-6 [&>h3]:mb-3 [&>h3]:text-xl [&>h3]:font-bold
                                        [&>ul]:list-disc [&>ul]:ml-5 [&>ul]:mb-4
                                        [&>ol]:list-decimal [&>ol]:ml-5 [&>ol]:mb-4">
                            {ringkasan && (
                                <p className="text-lg italic border-l-4 border-[#0D9488] pl-4 mb-6 text-gray-700">
                                    {ringkasan}
                                </p>
                            )}
                            {konten ? (
                                <div dangerouslySetInnerHTML={{ __html: konten }} />
                            ) : (
                                <p className="text-gray-500 italic">Konten tidak tersedia.</p>
                            )}
                        </div>

                        {/* File Lampiran (jika ada) */}
                        {berita.file_lampiran && (
                            <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <h3 className="font-semibold text-[#0F4C3A] mb-3">
                                    📎 File Lampiran
                                </h3>
                                <a
                                    href={getImageUrl(berita.file_lampiran)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-gray-100 transition-colors border"
                                >
                                    <span className="text-sm text-gray-700 truncate">
                                        {berita.file_name || 'Download File'}
                                    </span>
                                    <span className="text-[#0D9488] font-medium text-sm">Unduh</span>
                                </a>
                            </div>
                        )}

                        {/* ============ SHARE BAWAH ============ */}
                        <div className="mt-10 pt-6 border-t border-gray-200">
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="text-gray-600 font-semibold text-sm">
                                    Bagikan berita ini ke:
                                </span>
                                <button
                                    onClick={() => handleShare('facebook')}
                                    className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:scale-110 transition-all"
                                    title="Facebook"
                                >
                                    <FaFacebookF size={14} />
                                </button>
                                <button
                                    onClick={() => handleShare('twitter')}
                                    className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1DA1F2] text-white hover:scale-110 transition-all"
                                    title="Twitter"
                                >
                                    <FaTwitter size={14} />
                                </button>
                                <button
                                    onClick={() => handleShare('whatsapp')}
                                    className="w-9 h-9 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:scale-110 transition-all"
                                    title="WhatsApp"
                                >
                                    <FaWhatsapp size={14} />
                                </button>
                                <button
                                    onClick={() => handleShare('telegram')}
                                    className="w-9 h-9 flex items-center justify-center rounded-full bg-[#0088CC] text-white hover:scale-110 transition-all"
                                    title="Telegram"
                                >
                                    <FaTelegramPlane size={14} />
                                </button>
                            </div>
                        </div>

                        {/* Views info */}
                        <p className="text-xs text-gray-400 mt-6">
                            👁️ {views} kali dilihat
                        </p>
                    </div>
                </article>
            </div>
        </div>
    );
};

export default BeritaDetail;